import {
  leadsLogsListQueryKey,
  leadsOpportunitiesListQueryKey,
  opportunitiesDuplicateCreateMutation,
  opportunitiesListQueryKey,
  OpportunityPipelineReadable,
  ReturnOpportunityReadable
} from '@crm/api'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { TableActions } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, MenuProps } from 'antd'

import { Call, Copy, Edit2, ProfileAdd, Sms, Trash } from 'iconsax-react'

import { useParams } from 'react-router-dom'

import { ModalState } from './types'

type Props = {
  opportunity: ReturnOpportunityReadable | OpportunityPipelineReadable
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>
}

export const OpportunityActionsButton = ({
  opportunity,
  setModalState
}: Props) => {
  const { t } = useLocales()
  const navigate = useAppNavigate()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { id: leadId } = useParams()

  const { notification } = App.useApp()

  const { mutate: duplicateMutate } = useMutation({
    ...opportunitiesDuplicateCreateMutation(),
    onSuccess: (opportunity) => {
      invalidateQueries([
        leadsOpportunitiesListQueryKey({ path: { lead_id: String(leadId) } }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesListQueryKey()
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })

      navigate(
        paths.opportunitiesDetails.edit(
          String(opportunity?.id),
          String(opportunity?.lead?.id)
        )
      )
    },

    onError: (error) => showApiErrors(error)
  })

  const actionItems = (
    opportunity: ReturnOpportunityReadable | OpportunityPipelineReadable
  ): MenuProps['items'] => [
    ...('contact' in opportunity && opportunity?.contact?.id
      ? [
          {
            label: t('logs.call'),
            key: 'call',
            icon: <Call size={20} />,
            onClick: () => setModalState({ opportunity, type: 'call' })
          }
        ]
      : []),
    ...('contact' in opportunity && opportunity?.contact?.email
      ? [
          {
            label: t('logs.email'),
            key: 'email',
            icon: <Sms size={20} />,
            onClick: () =>
              window.open(`mailto:${opportunity?.contact?.email}`, '_blank')
          }
        ]
      : []),
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () =>
        navigate(
          paths.opportunitiesDetails.edit(
            String(opportunity?.id),
            String(opportunity?.lead?.id)
          )
        )
    },
    {
      label: t('buttons.duplicate'),
      key: 'duplicate',
      icon: <Copy size={20} />,
      onClick: () => {
        duplicateMutate({
          path: { id: Number(opportunity.id) }
        })
      }
    },
    {
      label: t('lead.convert_to_client'),
      key: 'convert',
      icon: <ProfileAdd size={20} />,
      onClick: () => setModalState({ opportunity, type: 'convert' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ opportunity, type: 'delete' })
    }
  ]

  return (
    <TableActions
      options={actionItems(opportunity)}
      popupRender={(originNode) => (
        <div onPointerDown={(e) => e.stopPropagation()}>{originNode}</div>
      )}
    />
  )
}
